import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import customerApi from '../../../services/customerApi';
import Notification from '../../../components/notification/notification';
import { DataTableSkeleton } from '../../../data/columns';
import {
  EmptyState,
  StatCard,
  TWButton,
  TWDataTable,
} from '../../../components/ui';
import { formatCurrency, formatPhoneNumber } from '../../../utils/baseUtils';
import NoData2 from '../../../assets/no-data-2.svg';
import { buildCustomerOrderColumns } from './columns';

const sortedByDateDesc = (orders) =>
  [...orders].sort((a, b) => new Date(b.createdate) - new Date(a.createdate));

// Cash orders are never charged through Stripe, so they stay "Not Paid" (status 0).
// Only Stripe-charged (Paid) orders should count toward the customer's spend, matching
// the customer list aggregation on the backend.
const PAID_PAYMENT_STATUS = 20;

const computeKpis = (customer) => {
  const orders = customer?.orders || [];
  const paidOrders = orders.filter(
    (o) => o.paymentstatus === PAID_PAYMENT_STATUS,
  );
  const orderCount = paidOrders.length;

  const totals = paidOrders.reduce(
    (acc, o) => {
      const net = (o.total || 0) - (o.refund || 0);
      acc.spend += net;
      acc.tip += o.tip || 0;
      acc.subtotal += o.subtotal || 0;
      return acc;
    },
    { spend: 0, tip: 0, subtotal: 0 },
  );

  const lifetimeSpend =
    typeof customer?.lifetimespend === 'number' && customer.lifetimespend > 0
      ? customer.lifetimespend
      : totals.spend;

  const averageSpend = orderCount > 0 ? lifetimeSpend / orderCount : 0;

  const averageTipPct =
    totals.subtotal > 0 ? (totals.tip / totals.subtotal) * 100 : 0;

  const lastOrderDate =
    customer?.lastorderdate ||
    (orders[0] ? sortedByDateDesc(orders)[0]?.createdate : null);

  return {
    orderCount,
    lifetimeSpend,
    averageSpend,
    averageTipPct,
    lastOrderDate,
  };
};

const relativeTime = (date) => {
  if (!date) return '—';
  return moment(date).fromNow();
};

const CustomerDetail = () => {
  const { id } = useParams();

  const [customer, setCustomer] = useState({});
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState({ customerLoading: true });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: 0,
  });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const getCustomer = async () => {
    setLoading((prev) => ({ ...prev, customerLoading: true }));
    setCustomer({});
    try {
      const list = await customerApi.getById(`?id=${id}`);
      if (list.data.data) {
        const data = list.data.data;
        setCustomer(data);
        const sorted = sortedByDateDesc(data.orders || []);
        setFilteredList(sorted.slice(0, pagination.pageSize));
        setPagination((prev) => ({ ...prev, totalRecords: sorted.length }));
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, customerLoading: false }));
    }
  };

  const handleChangePage = (pageNumber, pageSize) => {
    setPagination((prev) => ({ ...prev, pageNumber, pageSize }));
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setFilteredList(
      sortedByDateDesc(customer.orders || []).slice(startIndex, endIndex),
    );
  };

  useEffect(() => {
    getCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = useMemo(() => computeKpis(customer), [customer]);

  const openStripe = () => {
    if (!customer?.customerid) {
      setNotf({
        display: true,
        type: 'error',
        message: 'There is no Stripe customer ID for this customer.',
      });
      return;
    }
    window.open(
      process.env.REACT_APP_BASE_STRIPE_CUSTOMER_URL + customer.customerid,
      '_blank',
    );
  };

  const hasName = Boolean(customer?.fullname);
  const hasEmail = Boolean(customer?.email);
  const hasPhone = Boolean(customer?.phone);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-10 lg:py-10">
      <div className="mx-auto max-w-6xl space-y-8">
        <Link
          to="/dashboard/customers"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          <ArrowLeftIcon className="h-3.5 w-3.5" />
          Back to Customers
        </Link>

        {loading.customerLoading ? (
          <CustomerDetailSkeleton />
        ) : (
          <>
            <section
              className="reveal flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
              aria-label="Customer summary"
            >
              <div className="min-w-0 flex-1 lg:max-w-md">
                <h1
                  className={
                    hasName
                      ? 'truncate text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-4xl'
                      : 'text-3xl font-light italic tracking-tight text-slate-300 dark:text-slate-600 sm:text-4xl'
                  }
                >
                  {customer.fullname || 'add name'}
                </h1>
                <div className="mt-3 flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500">
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  <span className="italic">add note</span>
                </div>
              </div>

              <div className="hidden h-16 w-px shrink-0 bg-slate-200 dark:bg-slate-700 lg:block" />

              <div className="flex flex-col gap-2.5 text-sm lg:max-w-xs lg:flex-1">
                <div className="flex items-center gap-2.5">
                  <PhoneIcon
                    className={
                      hasPhone
                        ? 'h-4 w-4 text-slate-500 dark:text-slate-400'
                        : 'h-4 w-4 text-slate-300 dark:text-slate-600'
                    }
                  />
                  {hasPhone ? (
                    <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">
                      {formatPhoneNumber(customer.phone)}
                    </span>
                  ) : (
                    <span className="italic text-slate-400 dark:text-slate-500">
                      No phone number
                    </span>
                  )}
                  {customer.ismobile && (
                    <span className="inline-flex items-center gap-0.5 rounded-md bg-blue-50 px-1.5 py-0.5 text-[0.6rem] font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20">
                      <DevicePhoneMobileIcon className="h-3 w-3" />
                      App
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2.5">
                  <EnvelopeIcon
                    className={
                      hasEmail
                        ? 'h-4 w-4 text-slate-500 dark:text-slate-400'
                        : 'h-4 w-4 text-slate-300 dark:text-slate-600'
                    }
                  />
                  {hasEmail ? (
                    <a
                      href={`mailto:${customer.email}`}
                      className="truncate font-medium text-slate-700 transition hover:text-blue-600 dark:text-slate-200 dark:hover:text-sky-400"
                    >
                      {customer.email}
                    </a>
                  ) : (
                    <span className="italic text-slate-400 dark:text-slate-500">
                      No email address
                    </span>
                  )}
                </div>
                {customer?.customerid && (
                  <div className="pt-1">
                    <TWButton
                      icon={ArrowTopRightOnSquareIcon}
                      label="Show on Stripe"
                      onClick={openStripe}
                      size="sm"
                    />
                  </div>
                )}
              </div>
            </section>

            <section
              className="reveal grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
              aria-label="Customer key figures"
            >
              <StatCard
                label="Last Order"
                value={relativeTime(kpis.lastOrderDate)}
                style={{ animationDelay: '0ms' }}
              />
              <StatCard
                label="Lifetime Spend"
                value={formatCurrency(kpis.lifetimeSpend)}
                style={{ animationDelay: '60ms' }}
              />
              <StatCard
                label="Average Spend"
                value={formatCurrency(kpis.averageSpend)}
                style={{ animationDelay: '120ms' }}
              />
              <StatCard
                label="Average Tip"
                value={`${Math.round(kpis.averageTipPct)}%`}
                style={{ animationDelay: '180ms' }}
              />
            </section>

            {filteredList.length > 0 ? (
              <section className="reveal" aria-label="Customer orders">
                <TWDataTable
                  columns={buildCustomerOrderColumns()}
                  data={filteredList}
                  rowKey="id"
                  rowClickPath={(order) => `/dashboard/orders/${order.id}`}
                  pagination={
                    pagination.totalRecords > 0
                      ? {
                          current: pagination.pageNumber,
                          pageSize: pagination.pageSize,
                          total: pagination.totalRecords,
                          showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} results`,
                          onChange: handleChangePage,
                        }
                      : undefined
                  }
                />
              </section>
            ) : (
              <EmptyState
                image={NoData2}
                title="No orders"
                description="This customer hasn't placed any orders yet."
              />
            )}
          </>
        )}
      </div>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

const CustomerDetailSkeleton = () => (
  <div className="space-y-8">
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex-1 space-y-3 lg:max-w-md">
        <div className="h-9 w-56 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="h-4 w-24 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="flex flex-col gap-2 lg:max-w-xs lg:flex-1">
        <div className="h-4 w-40 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="h-4 w-48 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-24 animate-pulse rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/70"
        />
      ))}
    </div>
    <DataTableSkeleton
      rows={6}
      headers={[
        { label: 'Receipt#', w: 'w-16' },
        { label: 'Details', w: 'w-24' },
        { label: 'Payment Status', w: 'w-20', shape: 'pill' },
        { label: 'Time', w: 'w-24' },
        { label: 'Order Type', w: 'w-16', shape: 'pill' },
        { label: 'Total', w: 'w-20' },
        { label: 'Order Status', w: 'w-24', shape: 'pill' },
      ]}
    />
  </div>
);

export default CustomerDetail;
